import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment.development';
import { STORAGE_ENUM } from '../../../auth/enums/storage.enum';
import { SessionService } from '../../../auth/services/session/session.service';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ActionPlansService {
  private _http = inject(HttpClient);
  private _sessionService = inject(SessionService);
  private _localStorage = inject(LocalStorageService);

  private _baseUrl = `${environment.apiUrl}/administrative`;

  getActionPlans(filters: any): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error getting action plans'));
    }

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService.Customer.id;
    let actionPlansUrl = `${this._baseUrl}/mip/action-plans/?event__customer=${customerId}`;

    if (isMainCustomer) {
      actionPlansUrl += `&event__customer__main_customer=${customerId}`;
    }

    if (filters.search) {
      actionPlansUrl += `&search=${filters.search}`;
    }

    if (filters.page) {
      actionPlansUrl += `&page=${filters.page}`;
    }

    if (filters.page_size) {
      actionPlansUrl += `&page_size=${filters.page_size}`;
    }

    if (filters.status) {
      actionPlansUrl += `&status=${filters.status}`;
    }

    if (filters.root_cause) {
      actionPlansUrl += `&root_cause=${filters.root_cause instanceof Object ? filters.root_cause.id : filters.root_cause}`;
    }

    if (filters.qualification) {
      actionPlansUrl += `&qualification=${filters.qualification instanceof Object ? filters.qualification.id : filters.qualification}`;
    }

    if (filters.event) {
      actionPlansUrl += `&event=${filters.event instanceof Object ? filters.event.id : filters.event}`;
    }

    return this._http.get<any>(actionPlansUrl, {
      headers
    });
  }

  createActionPlan(data: any): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error al crear el plan de acción'));
    }

    return this._http.post<any>(`${this._baseUrl}/mip/action-plans/`, data, {
      headers
    });
  }

  updateActionPlan(data: any, actionPlanId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error al actualizar el plan de acción'));
    }

    return this._http.patch<any>(`${this._baseUrl}/mip/action-plans/${actionPlanId}/`, data, {
      headers
    });
  }

  deleteActionPlan(actionPlanId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error al eliminar el plan de acción'));
    }

    return this._http.delete<any>(`${this._baseUrl}/mip/action-plans/${actionPlanId}/`, {
      headers
    });
  }

  getRootCauses(query?: string): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/catalogs/root-causes/?page=1&page_size=10`;

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<any[]>(url, {
      headers
    });
  }

  getQualifications(query?: string): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/catalogs/qualifications/?page=1&page_size=10`;

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<any[]>(url, {
      headers
    });
  }

  getEvents(query?: string): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService.Customer.id;
    let url = `${this._baseUrl}/events/?customer=${customerId}&page=1&page_size=10`;

    if (isMainCustomer) {
      url += `&customer__main_customer=${customerId}`;
    }

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<any[]>(url, {
      headers
    });
  }
}
